import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * Custom Contact Form
 * - Envia e-mail de notificação para a equipa
 * - Sincroniza automaticamente o contacto com o Brevo CRM
 */

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const CustomContactForm = () => {
  const { t } = useTranslation();
  
  const [formData, setFormData] = useState({
    firstname: '',
    lastname: '',
    email: '',
    phone: '',
    message: ''
  });
  
  const [status, setStatus] = useState('idle'); // idle | sending | success | error
  const [errorMessage, setErrorMessage] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('sending');
    setErrorMessage('');

    try {
      const response = await fetch(`${BACKEND_URL}/api/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData)
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Erro ao enviar mensagem');
      }

      // Sucesso
      setStatus('success');
      setFormData({
        firstname: '',
        lastname: '',
        email: '',
        phone: '',
        message: ''
      });

      // Reset da mensagem de sucesso apos 5 segundos
      setTimeout(() => setStatus('idle'), 5000);

    } catch (error) {
      console.error('Contact form error:', error);
      setStatus('error');
      setErrorMessage(error.message || 'Erro ao enviar mensagem. Tente novamente.');
    }
  };

  return (
    <div className="w-full">
      <div className="bg-white rounded-lg shadow-lg overflow-hidden">
        <div className="p-6 bg-blue-600">
          <h3 className="text-2xl font-bold text-white">
            Contacta-nos
          </h3>
          <p className="text-blue-100 mt-2">
            Preencha o formulário e entraremos em contacto em breve
          </p>
        </div>

        <div className="p-6">
          {/* Mensagem de Sucesso */}
          {status === 'success' && (
            <div className="mb-6 p-4 bg-green-100 border border-green-400 text-green-700 rounded-lg">
              <p className="font-semibold">✅ Mensagem enviada com sucesso!</p>
              <p className="text-sm mt-1">Obrigado pelo contacto. Responderemos em breve.</p>
            </div>
          )}

          {/* Mensagem de Erro */}
          {status === 'error' && (
            <div className="mb-6 p-4 bg-red-100 border border-red-400 text-red-700 rounded-lg">
              <p className="font-semibold">❌ Erro ao enviar mensagem</p>
              <p className="text-sm mt-1">{errorMessage}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Nome */}
            <div>
              <label htmlFor="firstname" className="block text-gray-700 font-semibold mb-2">
                Nome *
              </label>
              <input
                type="text"
                id="firstname"
                name="firstname"
                value={formData.firstname}
                onChange={handleChange}
                required
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Nome"
              />
            </div>

            {/* Apelido */}
            <div>
              <label htmlFor="lastname" className="block text-gray-700 font-semibold mb-2">
                Apelido
              </label>
              <input
                type="text"
                id="lastname"
                name="lastname"
                value={formData.lastname}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Último Nome"
              />
            </div>

            {/* Email */}
            <div>
              <label htmlFor="email" className="block text-gray-700 font-semibold mb-2">
                Email *
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="EMAIL"
              />
            </div>

            {/* Telefone */}
            <div>
              <label htmlFor="phone" className="block text-gray-700 font-semibold mb-2">
                Telefone
              </label>
              <input
                type="tel"
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="TELEFONE"
              />
            </div>

            {/* Mensagem */}
            <div>
              <label htmlFor="message" className="block text-gray-700 font-semibold mb-2">
                Mensagem *
              </label>
              <textarea
                id="message"
                name="message"
                value={formData.message}
                onChange={handleChange}
                required
                rows="5"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Escreva a sua mensagem aqui..."
              />
            </div>

            {/* Botao de Submissao */}
            <button
              type="submit"
              disabled={status === 'sending'}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white py-3 px-6 rounded-lg font-semibold transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
            >
              {status === 'sending' ? (
                <span className="flex items-center justify-center">
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  A enviar...
                </span>
              ) : (
                'Enviar Mensagem'
              )}
            </button>

            <p className="text-xs text-gray-500 text-center mt-4">
              🔒 Os seus dados são protegidos e tratados de acordo com o RGPD.
            </p>
          </form>
        </div>
      </div>
    </div>
  );
};

export default CustomContactForm;